import { FC } from "react";
import { Link } from "react-router";

const PrivacyPolicy: FC = () => {
  return (
    <>
      <div className="d-flex flex-column gap-15">
        <div className="fs-18 fw-700 text-teal">Privacy Policy</div>
        <div className="privacy-content">
          <p>Date of Last Update: 27 August 2025</p>
          <p>
            This is the Privacy Policy of Jennifer Margaret Lloyd trading as
            Connected Crew (“we”, “us”, or “ our”).
          </p>
          <div className="fs-18 fw-700 text-teal">1. Introduction</div>
          <p>
            We manage Personal Information in accordance with the Privacy Act
            1988 and Australian Privacy Principles. “Personal Information” is
            information that we hold which is identifiable as being about you.
            This includes information such as your nam e, email address and any
            other type of information that can reasonably identify an
            individual, either directly or indirec tly.
          </p>
          <p>
            This policy applies to information collected by Connected Crew and
            sets out how we collect and treat your Personal Information.
          </p>
          <p>
            We only collect information that is reasonably necessary for the
            proper performance of our activities or functions.
          </p>
          <p>
            We do not collect Personal Information just because we think it
            could be useful at some future stage if we have no present need for
            it.
          </p>
          <p>
            We may decline to collect unsolicited Personal Information from or
            about you and take steps to purge it from our systems.
          </p>
          <p>
            By following the links in this document, you will be able to find
            out how we manage your Personal Information as an APP Entity under
            the{" "}
            <Link
              to="https://www.oaic.gov.au/images/documents/privacy/privacy-resources/privacy-fact-sheets/privacy-fact-sheet-17-australian-privacy-principles.pdf"
              target="_blank"
            >
              Australian Privacy Principles (APPs ).
            </Link>{" "}
            If you have any questions, please{" "}
            <a href="mailto:hello@connectedcrew.com.au">contact us</a>.
          </p>
          <p>
            You may contact us in writing at{" "}
            <a href="mailto:hello@connectedcrew.com.au">
              hello@connectedcrew.com.au
            </a>{" "}
            for further information about this Privacy Policy.
          </p>
          <div className="fs-18 fw-700 text-teal">1.1 APP Entity</div>
          <p>
            Connected Crew manages Personal Information, as an APP Entity, under
            the{" "}
            <a href="http://www.oaic.gov.au/images/documents/privacy/privacy-resources/privacy-fact-sheets/privacy-fact-sheet-17-australian-privacy-principles.pdf">
              Australian Privacy Principles (APPs )
            </a>
            .
          </p>
          <p>
            Because we are a contracted service provider to a range of
            Commonwealth, State and Territory government agencies, it sometimes
            becomes necessary for us to collect and manage Personal Information
            as an Agency under different privacy arrangements.
          </p>
          <p>
            If you wish to know whether this applies to you, please{" "}
            <a href="mailto:hello@connectedcrew.com.au">contact us</a>.
          </p>
          <div className="fs-18 fw-700 text-teal">1.2 Information Flow</div>
          <p>When we collect your Personal Information: </p>
          <ul>
            <li>
              We check that it is reasonably necessary for our functions or
              activities as an employment agency and on -hire firm ;
            </li>
            <li>
              We check that it is current, complete and accurate. This will
              sometimes mean that we have to cross check the information that we
              collect from you with third parties ;
            </li>
            <li>
              We record and hold your information in our Information Record
              System and your information may be disclosed to overseas
              recipients ;
            </li>
            <li>
              We retrieve your information when we need to use or disclose it
              for our functions and activities. At that time, we check that it
              is current, complete, accurate and relevant. This will sometimes
              mean that we have to cross check the information that we collec t
              from you with third parties once again – especially if some time
              has passed since we last checked ;
            </li>
            <li>
              Subject to some exceptions, we permit you to access your Personal
              Information in accordance with APP:12 of the (
              <a
                href="https://www.oaic.gov.au/individuals/privacy-fact-sheets/general/privacy-fact-sheet-17-australian-privacy-principles"
                target="_blank"
                rel="noopener noreferrer"
              >
                APPs
              </a>
              );
            </li>
            <li>
              We correct or attach associated statements to your Personal
              Information in accordance with APP:13 of the (
              <a
                href="https://www.oaic.gov.au/individuals/privacy-fact-sheets/general/privacy-fact-sheet-17-australian-privacy-principles"
                target="_blank"
                rel="noopener noreferrer"
              >
                APPs
              </a>
              ); and
            </li>
            <li>
              We destroy or de -identify your Personal Information when it is no
              longer needed for any purpose which it may be used or disclosed
              provided that it is lawful for us to do so. We do not destroy or
              de -identify information that is contained in a{" "}
              <a
                href="http://www.austlii.edu.au/au/legis/cth/consol_act/aa198398/s3.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                Commonwealth Record
              </a>
            </li>
          </ul>
          <div className="fs-18 fw-700 text-teal">
            2. Kinds of Information we hold and collect
          </div>
          <p>
            Personal Information that we collect and hold is information that is
            reasonably necessary for the proper performance of our functions and
            activities as an employment agency and is likely to differ depending
            on whether you are:
          </p>
          <ul>
            <li>A Workseeker ;</li>
            <li>A Client ; or</li>
            <li>A Referee</li>
          </ul>
          <p>
            Generally, we do not collect Sensitive Information about you unless
            required by law or where it is required in order for us t o provide
            you with the services. “Sensitive Information” includes information
            relating to race, political or religious beliefs, sexual orientation
            and sexual life, criminal convictions, membership of professional or
            trade associations or unions, biometric information or information
            about your affiliation with certain organisations such as
            professional associations. If Sensitive Inform ation is required, we
            will limit its collection to the minimum amount required to perform
            our services.
          </p>
          <div className="fs-18 fw-700 text-teal">2.1 For Workseekers</div>
          <p>
            The type of information that we typically collect and hold about
            Workseekers is information that is necessary to assess amenability
            to work offers and work availability; suitability for placements; or
            to manage the performance in work obtained through us a nd includes
            but is not limited to :
          </p>
          <ul>
            <li>
              General contact and identification information such as name,
              address, email and phone number ;
            </li>
            <li>
              Information about your work history, education, experience,
              qualifications and skills ;
            </li>
            <li>
              Information about your eligibility to legally work within
              Australia, which may include copies of your passport, applicable
              visas or other relevant documentation ;
            </li>
            <li>
              Your tax file number and relevant bank and superannuation account
              information necessary to facilitate the payment of wages and
              superannuation contributions and to ensure appropriate taxation
              treatment ; and
            </li>
            <li>
              Other information that may be relevant in the circumstances or
              that you provide while interacting with us.
            </li>
          </ul>
          <div className="fs-18 fw-700 text-teal">2.2 For Clients</div>
          <p>
            The type of information that we typically collect and hold about
            Clients is information that is necessary to help us manage t he
            presentation and delivery of our services and includes but is not
            limited to :
          </p>
          <ul>
            <li>
              General contact and identification information such as name,
              address, email and phone number ;
            </li>
            <li>Details of current and past employment placement ;</li>
            <li>
              Your opinion of prospective of potential and/or successfully
              placed candidates ; and
            </li>
            <li>
              Other information that may be relevant in the circumstances or
              that you provide while interacting with us.
            </li>
          </ul>

          <div className="fs-18 fw-700 text-teal">2.3 For Referees</div>
          <p>
            The type of information that we typically collect and hold about
            Referees is information that is necessary to help to make
            determinations about the suitability of one of our Workseekers for
            particular jobs or particular types of work and includes but is not
            limited to :
          </p>
          <ul>
            <li>
              General contact and identification information such as name,
              address, email and phone number ;
            </li>
            <li>Details of current and past employment ;</li>
            <li>
              Your opinion of prospective of potential and/or successfully
              placed candidates ; and
            </li>
            <li>
              Other information that may be relevant in the circumstances or
              that you provide while interacting with us.
            </li>
          </ul>
          <div className="fs-18 fw-700 text-teal">3. Purposes</div>
          <p>
            We will use Personal Information only for the purposes that you
            consent to. This may include to:
          </p>
          <ul>
            <li>
              communicate with you about our services including but not limited
              to:
            </li>
            <li>work placement operations;</li>
            <li>recruitment functions;</li>
            <li>client and business relationship management; and</li>
            <li>marketing services.</li>
            <li>statistical purposes (including the use of cookies); and</li>
            <li>
              to confirm your identity and authority to provide references.
            </li>
          </ul>
          <p>
            If you withhold your Personal Information, it may not be possible
            for us to provide you with our services or for you to fully access
            our website.
          </p>
          <p>
            We may disclose your Personal Information to comply with legal
            requirements, such as a law, regulation, court order, subpoena ,
            warrant, legal proceedings or in response to a law enforcement
            agency request.
          </p>
          <p>
            If there is a change of control in our business or a sale or
            transfer of business assets, we reserve the right to transfer to the
            extent permissible at law our user databases, together with any
            Personal Information and non -personal information contained in
            those databases, without having to obtain your consent.
          </p>
          <div className="fs-18 fw-700 text-teal">
            4. How your Personal Information is collected
          </div>
          <p>
            We collect Personal Information from you in a variety of ways,
            including when you contact us with a question or enquiry, prov ide
            your resume or CV or when you access our website. We may use
            ‘cookies’ on our website. Cookies are an industry standard, and most
            major websites use them. A cookie is a small text file that our
            website may place on your computer as a tool to remember your
            preferences. You may refuse the use of cookies by selecting the
            appropriate settings in your browser.
          </p>
          <p>
            The means by which we will generally collect your Personal
            Information are likely to differ depending on whether you are a
            Workseeker, a Client or a Referee.
          </p>
          <p>
            Sometimes the technology that is used to support communications
            between us will provide Personal Information to us – see the section
            in this policy on Electronic Transactions.
          </p>
          <p>
            By providing us with Personal Information, you consent to the supply
            of that information subject to the terms of this Privacy Policy.
          </p>
          <p>
            See also the section on <strong>Photos & Images</strong>.
          </p>
          <div className="fs-18 fw-700 text-teal">4.1 For Workseekers</div>
          <p>
            Personal Information will be collected from you directly when you
            fill out and submit one of our application forms, apply to a
            position advertised on the Connected Crew website or any other
            information in connection with your application to us for work.
          </p>
          <p>Personal Information is also collected when:</p>
          <ul>
            <li>
              You submit an application form or your resume through a third
              -party website ;
            </li>
            <li>
              We interview you either over the phone, video conference or in
              person ;
            </li>
            <li>
              We undertake reference checks by inquiring with, or we otherwise
              receive references or performance feedback
            </li>
            <li>
              (whether negative or positive) from, any of your former or current
              employers, work colleagues, professional
            </li>
            <li>
              associations or registration bodies (refe rence checks are only
              undertaken with your consent) ;
            </li>
            <li>
              Social Networks and Web Searches – including LinkedIn, Seek and
              other industry specific job board search ;
            </li>
            <li>
              We receive results of any medical tests or criminal history
              checks, (which are only undertaken with your consent) ;
            </li>
            <li>
              We receive results from any competency tests in which you
              participate ;
            </li>
            <li>
              We receive any complaint from or about you in the workplace ;
            </li>
            <li>
              We receive information from your employer in relation to your
              employee benefits including rate or remuneration ;
            </li>
            <li>
              We receive information about a workplace accident in which you
              were involved; and
            </li>
            <li>
              We receive any information about any insurance investigation,
              litigation, registration or professional disciplinary matter,
            </li>
            <li>
              criminal matter, inquest or inquiry in which you were or are
              involved during, or in connection with, a work placement.
            </li>
          </ul>
          <p>
            We may also collect Personal Information about you from a range of
            publicly available sources including newspapers, journals,
            directories, the Internet and social media sites. When we collect
            Personal Information about you from publicly available sources for
            inclusion in our records we will manage the information in
            accordance with the APPs and our Privacy Policy.
          </p>
          <div className="fs-18 fw-700 text-teal">4.2 For Clients</div>
          <p>
            Personal information about you may be collected when you provide it
            directly to us.
          </p>
          <p>
            We may also collect Personal Information about you from a range of
            publicly available sources including newspapers, journals,
            directories, the Internet and social media sites. When we collect
            Personal Information about you from publicly available sources for
            inclusion in our records we will manage the information in
            accordance with the APPs and our Privacy Policy.
          </p>
          <div className="fs-18 fw-700 text-teal">4.3 For Referees</div>
          <p>
            Personal information about you may be collected when you provide it
            directly to us.
          </p>
          <p>
            We may also collect Personal Information about you from a range of
            publicly available sources including newspapers, journals,
            directories, the Internet and social media sites. When we collect
            Personal Information about you from publicly available sources for
            inclusion in our records we will manage the information in
            accordance with the APPs and our Privacy Policy.
          </p>
          <div className="fs-18 fw-700 text-teal">4.4 Photos & Images</div>
          <p>
            We will not request that you supply photographs, scan photo ID, or
            capture and retain video image data of you in cases where simply
            sighting photographs or proof of identity documents would be
            sufficient in the circumstances. In the event we retain photos or
            images of you for use in the recruitment process, they will be
            retained in accordance with this Privacy Policy and you may request
            that they be destroyed at any time.
          </p>
          <div className="fs-18 fw-700 text-teal">
            4.5 Electronic Transactions
          </div>
          <p>
            Sometimes, we collect Personal Information that individuals choose
            to give us via online forms or by email, for example when
            individuals:{" "}
          </p>
          <ul>
            <li>
              ask to be on an email list such as a job notification list ;
            </li>
            <li>
              register as a site user to access facilities on our site such as a
              job notification board ;
            </li>
            <li>
              make a written online enquiry or email us through our website ;
              and
            </li>
            <li>
              submit a resume or application by email or through our website .
            </li>
          </ul>
          <p>
            It is important that you understand that there are risks associated
            with use of the Internet and you should take all appropri ate steps
            to protect your Personal Information. It might help you to look at
            the OAIC’s resource on Internet Communications and other
            Technologies .
          </p>
          <p>
            You can contact us by land line telephone or post if you have
            concerns about making contact via the Internet.
          </p>
          <p>
            Our site may from time to time have links to other websites not
            owned or controlled by us. These links are meant for your
            convenience only. Links to third party websites do not constitute
            sponsorship or endorsement or approval of these websites. Please be
            aware that we are not responsible for the privacy practises of other
            such websites. We encourage our users to be aware, when they leave
            our website, to read the privacy statements of each and every
            website that collects personal identifia ble information.
          </p>
          <div className="fs-18 fw-700 text-teal">
            5. How your Personal Information is held
          </div>
          <p>
            Personal Information is held in our Information Record System until
            it is no longer needed for any purpose for which it may be used or
            disclosed at which time it will be de -identified or destroyed,
            provided that it is lawful for us to do so.
          </p>
          <p>
            We take a range of measures to protect your Personal Information
            from:
          </p>
          <ul>
            <li>Misuse, interference and loss; and</li>
            <li>Unauthorised access, modification or disclosure.</li>
          </ul>
          <p>
            You acknowledge that personal data that you submit for publication
            through our website or service may be available, via the internet,
            around the world. Whilst we will take appropriate steps to safeguard
            your personal data, we cannot prevent the use (or mi suse) of such
            personal data by others.
          </p>
          <div className="5.1 Our Information Record System"></div>
          <p>
            Our Information Record System is a recruitment CRM system that
            stores data electronically. Information is stored on a server in a
            secure data room in Melbourne/Sydney in Australia. However,
            information that we collect may from time to time be stored,
            processed in or transferred between parties or sites located in
            countries outside of Australia.
          </p>
          <p>
            If you forward Connected Crew an email via the email link on our
            website, or contact us directly to apply directly for a position,
            the information collected through the email will only be used for
            the purpose for which it is provided .
          </p>
          <p>
            Any digital records or email correspondence will also be stored in
            our Cloud storage provided by MS Outlook 365, whose server s are
            found within Australia.
          </p>
          <p>
            Any physical records of your information are securely stored in on
            Connected Crew premises in Australia . In the event that we open
            offices or facilities outside of Australia, any transfers to each of
            these countries will be protected by appropriate safeguards.
          </p>
          <div className="fs-18 fw-700 text-teal">5.2 Information Security</div>
          <p>
            All electronic data is protected by multi -layer security systems
            both physical and online including IPS, authentication and
            firewalls.
          </p>
          <p>
            The data in our Information Record System is securely stored with
            Job Adder Operations Pty Ltd. JobAdder are ISO -27001 Certified (
            international standard for Business Continuity Management Systems
            (BCMS) ) and their privacy policy can be located here .
          </p>
          <p>
            Any physical records of your Personal Information are securely
            stored on a Connected Crew premises in Australia. We de -identify
            Personal Information from our database or securely destroy physical
            copies as soon as the law permits, and provided that the information
            is no longer needed by Connected Crew .
          </p>
          <div className="fs-18 fw-700 text-teal">6. Disclosures</div>
          <p>
            We may disclose your Personal Information to any of our employees,
            officers, insurers, professional advisers, agents, supplie rs or
            subcontractors insofar as reasonably necessary for the purposes set
            out in this Privacy Policy.
          </p>
          <p>
            If we do disclose your Personal Information to a third party, we
            will protect it in accordance with this Privacy Policy. Any
            information stored with third party providers is subject to the
            privacy policies of those organisations and we will provide a ccess
            to those policies.
          </p>
          <p>
            We are committed to ensuring that the information you provide to us
            is secure. In order to prevent unauthorised access or disclosure, we
            have put in place suitable physical, electronic and managerial
            procedures to safeguard and secure information and prote ct it from
            misuse, interference, loss and unauthorised access, modification and
            disclosure.
          </p>
          <p>
            The transmission and exchange of information is carried out at your
            own risk. We cannot guarantee the security of any information that
            you transmit to us, or receive from us. Although we take measures to
            safeguard against unauthorised disclosures of infor mation, we
            cannot assure your that the Personal Information that we collect
            will not be disclosed in a manner that is inconsistent with this
            Privacy Policy.
          </p>
          <p>
            Your Personal Information will not be kept for longer than necessary
            for us to fulfil the specific purposes outlined in this Privacy
            Policy, and to allow us to comply with our legal requirements.
          </p>
          <div className="fs-18 fw-700 text-teal">
            6.1 Related Purpose Disclosures
          </div>
          <p>
            We outsource a number of services to contracted service suppliers
            (CSPs) from time to time. Our CSPs may see some of your Personal
            Information. Typically, our CSPs would include:
          </p>
          <ul>
            <li>Software solutions providers ;</li>
            <li>
              I.T. contractors and database designers and Internet service
              suppliers ;
            </li>
            <li>Legal and other professional advisors ;</li>
            <li>Insurance brokers, loss assessors and underwriters ;</li>
            <li>Superannuation fund managers ; and</li>
            <li>Background checking and screening agents</li>
          </ul>
          <p>
            We take reasonable steps to ensure that terms of service with our
            CSPs recognise that we are bound by obligations to protect the
            privacy of your Personal Information and that they will not do
            anything that would cause us to breach those obligations.
          </p>
          <div className="fs-18 fw-700 text-teal">
            6.2 Cross -Border Disclosures
          </div>
          <p>
            Some of your Personal Information is likely to be disclosed to
            overseas recipients. We cannot guarantee that any recipient of your
            Personal Information will protect it to the standard to which it
            ought to be protected. The costs and difficulties of enforcement of
            privacy rights in foreign jurisdictions and the impracticability of
            attempting to enforce such rights in some jurisdictions will mean
            that in some instances, we will need to seek your consent to
            disclosure.
          </p>
          <p>
            For users in Australia, Microsoft OneDrive stores data primarily in
            Microsoft’s Australian data centers located in Sydney and Melbourne
            to support data residency and compliance with local laws. Microsoft
            also operates a global network of data centers acros s continents
            —including North America, Europe, and Asia. Please click here to
            read Microsoft’s policies.
          </p>
          <p>
            Amazon Web Services (AWS) stores data in the AWS Asia Pacific
            Region, based in Melbourne, Australia. Their ISO 27018 International
            code of practice focuses on protection of personal data in the
            cloud. Please click here to read Amazon Web Services (AWS) policies.
          </p>
          <p>
            The likely countries, type of information disclosed, and recipients
            are indicated, so far as is practicable, in the following table:
          </p>
          <table className="table table-bordered">
            <tbody>
              <tr>
                <td style={{ textWrap: "nowrap", width: "auto" }}>Country</td>
                <td>Type of Information</td>
                <td>Likely Recipients</td>
              </tr>
              <tr>
                <td>
                  Australia, New Zealand, North America, Europe, and Asia.
                </td>
                <td>
                  Personal data (including name, address, contact numbers)
                  stored on servers located in the countries listed.
                </td>
                <td>
                  IT Personnel and Contractors of our technology providers who
                  may access data for maintenance and software upgrade purposes.
                </td>
              </tr>
            </tbody>
          </table>
          <div className="7. Access & Correction"></div>
          <p>
            You may request details of Personal Information that we hold about
            you in accordance with the provisions of the Privacy Act 1988 (Cth).
            If you would like a copy of the information which we hold about you
            or believe that any information we hold on yo u is inaccurate, out
            of date, incomplete, irrelevant or misleading, please email us at
            hello@connectedcrew.com.au .
          </p>
          <p>
            We reserve the right to refuse to provide you with information that
            we hold about you, in certain circumstances set out in th e Privacy
            Act 1988 (Cth), or any other applicable law.
          </p>
          <p>
            Except to the extent required by applicable law, you are not
            authorised to gain access to evaluative opinion material obtaine d
            confidentially in the course of performing our reference checks.
            Alternatively, we may obtain consent from the relevant refer ees
            before sharing this information with you. You can request the
            deletion of such material, however you are not authorised to obtain
            access to it.
          </p>
          <div className="fs-18 fw-700 text-teal">7.1 Access Policy</div>
          <p>
            If you wish to obtain access to your Personal Information you should
            contact our Privacy Co -Ordinator. You will need to be in a position
            to verify your identity .
          </p>
          <p>
            Requests to access to your Personal Information will be acknowledged
            within 5 working days . Where possible such access will be granted
            within 30 working days . In the event there are circumstances
            restricting access within this time frame Connected Crew will advise
            you of these restrictions and communicate the revised timeframe
          </p>
          <p>
            Where lawful, you will be responsible for any costs or charges
            incurred by us to obtain any of your information.
          </p>
          <div className="fs-18 fw-700 text-teal">7.2 Correction Policy</div>
          <p>
            If you find that Personal Information that we hold about you is
            inaccurate, out of date, incomplete, irrelevant or misleading, you
            can ask us to correct it by contact us .
          </p>
          <p>
            We will take such steps as are reasonable in the circumstances to
            correct that information to ensure that, having regard to t he
            purpose for which it is held, the information is accurate, up to
            date, complete, relevant and not misleading.
          </p>
          <p>
            If we have disclosed Personal Information about you that is
            inaccurate, out of date, incomplete, irrelevant or misleading, you
            can ask us to notify the third parties to whom we made the
            disclosure and we will take such steps (if any) as are reasonable in
            the circumstances to give that notification unless it is
            impracticable or unlawful to do so.
          </p>
          <p>
            Requests to correct your Personal Information will be acknowledged
            within 5 working days . Where possible such corrections will be made
            within 30 working days . In the event there are circumstances
            restricting access within this time frame Connected Crew will advise
            you of these restrictions and communicate the revised timeframe
          </p>
          <p>
            Where lawful, you will be responsible for any costs or charges
            incurred by us to correct any of your information.
          </p>
          <div className="fs-18 fw-700 text-teal">
            8. Notifiable Data Breach
          </div>
          <p>An Eligible data breach occurs when:</p>
          <ul>
            <li>
              There is unauthorised access to, or unauthorised disclosure of
              Personal Information that we hold; and
            </li>
            <li>
              This is likely to result in serious harm to one or more
              individuals; and
            </li>
            <li>
              We have not been able to prevent the likely risk of serious harm
              with remedial actions (serious harm may be psychological,
              emotional, physical or reputational).
            </li>
          </ul>
          <p>
            If an Eligible Data Breach is found, we will complete the necessary
            procedures as prescribed by our internal Company Notifiable Data
            Breach Plan . You may request access to this plan by contacting us
            at hello@connectedcrew.com.au
          </p>
          <div className="fs-18 fw-700 text-teal">9. Complaints</div>
          <p>
            {" "}
            If you have any complaints about our privacy practices, please feel
            free to send in details of your complaints to
            hello@connectedcrew.com.au . We take complaints very seriously and
            will respond as soon as practicable after receiving written notice
            of your complaint and within 30 days at the latest.
          </p>
          <div className="fs-18 fw-700 text-teal">
            10. Changes to Privacy Policy
          </div>
          <p>
            Please be aware that we may change this Privacy Policy in the
            future. We may modify this Privacy Policy at any time, in our s ole
            discretion and all modifications will be effective immediately upon
            our posting of the modifications on our website. Please c heck back
            from time to time to review our Privacy Policy.
          </p>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
